/**
 * MOMENTEN — firmans lista över vad man gör, och den enda skärm som skriver den.
 *
 * VARFÖR DEN MÅSTE FINNAS FÖRE ALLT ANNAT. TimeEntryWrite bär ett task_id, och
 * en ny firma startar med NOLL moment. Utan den här skärmen möter varje ny kund
 * väggen i Tidny.tsx — "Firman har inga moment än" — och appen kan inte
 * rapportera en enda timme. Momentlistan är alltså inte en inställning bland
 * andra; den är förutsättningen för produkten.
 *
 * MOMENTEN HAMNAR ORDAGRANT PÅ KUNDENS FAKTURA. Det är hela skälet till att de
 * finns (appboken: "Det montören bockar i blir det kunden läser"), och därför
 * skrivs texten här av chefen och aldrig av montören ute på jobbet. Skärmen
 * säger det rakt ut, för det är skillnaden mellan "Demontering av gammal
 * utkastare" och "wc".
 *
 * TVÅ FÄLT, INTE FLER. TaskWrite bär `name` och `rot_eligible`, punkt. Appboken
 * föreslår dessutom en tid per moment och listor per jobbtyp — bra idéer som
 * INTE står i kontraktet, och det som inte står i kontraktet uppfinns inte i en
 * skärm. Se LUCKOR i överlämningen.
 *
 * BARA CHEFEN. 'put:task' och 'delete:task' är MANAGER_ONLY i SYNC_RULES, och
 * bokföraren står inte i en enda rad där (beslut 07) trots att han har full vy.
 * Skärmen ligger därför i läge 'lag' — en montör kan inte ens nå adressen — och
 * bokföraren möts av ett besked i stället för fält, precis som i NyOrder.tsx.
 * Listan får han se: han stämmer av fakturaunderlag mot bokföringen.
 *
 * OP_ID MYNTAS EN GÅNG OCH ÄNDRAS ALDRIG (beslut 09). När Spara trycks fryses en
 * PLAN — ett op_id och en kropp — och fälten låses. En omsändning skickar SAMMA
 * op_id med SAMMA kropp och får därför 'duplicate' i stället för ett andra
 * moment med samma namn.
 */

import { useState } from 'react';
import type { SyncOp, TaskId, TaskRow } from '@delat/types';
import {
  Falt,
  Felruta,
  Knapp,
  Kryssrad,
  Laddar,
  Listrad,
  Snabbrad,
  Tomtext,
  Yta,
} from '../ui';
import type { Klartidfel } from '../api';
import { useInloggad } from '../app/session';
import { glomAllaFirmaminnen } from '../app/firmaminnen';
import {
  MAX_MOMENTNAMN_TECKEN,
  andraMomentOp,
  arKlar,
  forstaMisslyckade,
  iBokstavsordning,
  namnetFinns,
  nyttMomentId,
  nyttMomentOp,
  nyttOpId,
  raderaMomentOp,
  skickaOps,
  useMomentlager,
} from './moment-data';

/** Den frysta inlämningen. Se OP_ID i filhuvudet. */
interface Plan {
  readonly slag: 'ny' | 'andra' | 'radera';
  /** Momentets namn som det stod när planen frystes — används i knapptexten. */
  readonly namn: string;
  readonly op: SyncOp;
}

const ROTHJALP =
  'Kryssa i det som är arbete hemma hos kunden. Resa, materialhämtning och själva materialet ger inget ROT.';

export const Moment = () => {
  const { firmaId, arChef, arLast } = useInloggad();
  const { lage, laddaOm } = useMomentlager(firmaId);

  const [oppet, sattOppet] = useState(false);
  const [namn, sattNamn] = useState('');
  const [gerRot, sattGerRot] = useState(false);

  const [andrarId, sattAndrarId] = useState<TaskId | null>(null);
  const [andratNamn, sattAndratNamn] = useState('');
  const [andratRot, sattAndratRot] = useState(false);
  const [fragarOmRadering, sattFragarOmRadering] = useState(false);

  const [plan, sattPlan] = useState<Plan | null>(null);
  const [sparar, sattSparar] = useState(false);
  const [fel, sattFel] = useState<Klartidfel | null>(null);
  const [faltfel, sattFaltfel] = useState<string | null>(null);

  if (lage.status === 'laddar') return <Laddar rader={5} />;
  if (lage.status === 'fel') {
    return (
      <Felruta
        fel={lage.fel}
        atgard={
          <Knapp variant="piller" onClick={laddaOm}>
            Försök igen
          </Knapp>
        }
      />
    );
  }

  const lager = lage.lager;
  const moment = iBokstavsordning(lager.moment);
  const last = plan !== null;
  const andrarRad: TaskRow | null =
    andrarId === null ? null : (moment.find((m) => m.id === andrarId) ?? null);

  /** Allt som ska nollas när en inlämning gått igenom. */
  const stang = (): void => {
    sattPlan(null);
    sattFel(null);
    sattFaltfel(null);
    sattNamn('');
    sattGerRot(false);
    sattOppet(false);
    sattAndrarId(null);
    sattFragarOmRadering(false);
    // ALLA LAGREN, inte bara momentlagret. Tidrapporten håller sin egen lista i
    // en annan modul (tidrapport/lager.ts), och utan den här raden kunde ett
    // nytt moment dröja upp till färskhetsfönstret innan montören såg det —
    // fast han står i trapphuset och väntar på just det momentet.
    glomAllaFirmaminnen();
    laddaOm();
  };

  const skicka = (aktuell: Plan): void => {
    sattPlan(aktuell);
    sattSparar(true);
    sattFel(null);
    void (async () => {
      const svar = await skickaOps(firmaId, [aktuell.op]);
      sattSparar(false);
      if (!svar.ok) {
        sattFel(svar.fel);
        return;
      }
      // 'applied' eller 'duplicate' — bägge betyder att raden står som vi ville.
      if (arKlar(svar.varde, aktuell.op.op_id)) {
        stang();
        return;
      }
      const opsfel = forstaMisslyckade(svar.varde);
      if (opsfel !== null) {
        sattFel(opsfel);
        return;
      }
      // Kuvertet kom tillbaka utan vår operation. Ska inte kunna hända — hämta
      // om och låt listan visa vad som faktiskt gäller i stället för att gissa.
      stang();
    })();
  };

  /** Klientens egen förkontroll. Servern gör den riktiga mot schemats CHECK. */
  const provaNamnet = (text: string, utom: TaskId | null): string | null => {
    const rensat = text.trim();
    if (rensat === '') return 'Momentet behöver ett namn.';
    if (rensat.length > MAX_MOMENTNAMN_TECKEN) {
      return `Högst ${MAX_MOMENTNAMN_TECKEN} tecken — det ska rymmas på en fakturarad.`;
    }
    if (namnetFinns(moment, rensat, utom)) return 'Det momentet finns redan i listan.';
    return null;
  };

  const laggTill = (): void => {
    const brist = provaNamnet(namn, null);
    if (brist !== null) {
      sattFaltfel(brist);
      return;
    }
    sattFaltfel(null);
    const rensat = namn.trim();
    skicka({
      slag: 'ny',
      namn: rensat,
      op: nyttMomentOp(nyttMomentId(), rensat, gerRot, nyttOpId()),
    });
  };

  const sparaAndringen = (rad: TaskRow): void => {
    const brist = provaNamnet(andratNamn, rad.id);
    if (brist !== null) {
      sattFaltfel(brist);
      return;
    }
    sattFaltfel(null);
    const rensat = andratNamn.trim();
    skicka({
      slag: 'andra',
      namn: rensat,
      op: andraMomentOp(rad, rensat, andratRot, nyttOpId()),
    });
  };

  const taBort = (rad: TaskRow): void => {
    sattFaltfel(null);
    skicka({
      slag: 'radera',
      namn: rad.name,
      op: raderaMomentOp(rad.id, rad.version, nyttOpId()),
    });
  };

  const oppnaAndring = (rad: TaskRow): void => {
    sattFel(null);
    sattFaltfel(null);
    sattOppet(false);
    sattFragarOmRadering(false);
    sattAndrarId(andrarId === rad.id ? null : rad.id);
    sattAndratNamn(rad.name);
    sattAndratRot(rad.rot_eligible === 1);
  };

  /**
   * OMSÄNDNINGEN SKICKAR PLANEN, ALDRIG ETT NYTT OP_ID. Trycket på "Försök igen"
   * får inte gå tillbaka genom laggTill() eller sparaAndringen(): de myntar ett
   * nytt op_id, och då blir omtaget en andra skrivning i stället för ett
   * uppspelat svar (beslut 09). Ett omtag på en fallen RADERING hade dessutom
   * hamnat i fel operation helt och hållet.
   */
  const forsokIgen = (): void => {
    if (plan === null) return;
    skicka(plan);
  };

  const FORSOKSTEXT: Readonly<Record<Plan['slag'], string>> = {
    ny: 'Försök spara igen',
    andra: 'Försök spara igen',
    radera: 'Försök ta bort igen',
  };

  /** Knappen som släpper den frysta planen och låter chefen rätta sig. */
  const slappPlanen = plan === null ? null : (
    <div style={{ marginTop: '0.5rem' }}>
      <Knapp
        variant="stor-tyst"
        onClick={() => {
          sattPlan(null);
          sattFel(null);
        }}
      >
        {plan.slag === 'radera' ? 'Låt momentet vara kvar' : 'Ändra uppgifterna'}
      </Knapp>
    </div>
  );

  return (
    <>
      {!arChef && (
        <Yta rubrik="Bara chefen bygger momentlistan">
          <p style={{ margin: 0 }}>
            Du ser hela laget, men moment, kunder och prislistor skrivs av chefen.
            Det är samma gräns som i attesten. Listan står kvar här så att du kan
            stämma av den mot fakturaunderlaget.
          </p>
        </Yta>
      )}

      <Yta rubrik="Firmans moment" rakning={`${moment.length} st`}>
        {moment.map((m) => (
          <Listrad
            key={m.id}
            titel={m.name}
            under={m.id === andrarId ? 'Ändras nedan' : undefined}
            varde={m.rot_eligible === 1 ? 'ROT' : undefined}
            onClick={arChef && !last ? () => oppnaAndring(m) : undefined}
          />
        ))}
        {moment.length === 0 && (
          <Tomtext>
            Firman har inga moment än. Ingen kan rapportera en timme förrän det
            finns minst ett — lägg upp de fem eller tio du gör oftast, så räcker
            det långt.
          </Tomtext>
        )}
        {moment.length > 0 && arChef && (
          <Tomtext>Tryck på ett moment för att ändra namnet eller ta bort det.</Tomtext>
        )}
      </Yta>

      {lager.avkortat && (
        <Tomtext>
          Firman har fler rader än som ryms i en hämtning, och listan kan därför
          sakna moment. Ladda om skärmen.
        </Tomtext>
      )}

      {/* --- Ändra ett moment ------------------------------------------- */}
      {arChef && andrarRad !== null && (
        <Yta rubrik="Ändra momentet">
          <Falt
            etikett="Vad heter momentet?"
            varde={andratNamn}
            onAndra={(t) => {
              sattFaltfel(null);
              sattAndratNamn(t);
            }}
            disabled={last}
            maxLength={MAX_MOMENTNAMN_TECKEN}
            placeholder="Demontering av gammal utkastare"
            hjalp="Rätta stavfel gärna. Skriv inte om momentet till något annat — se raden under."
            {...(faltfel === null ? {} : { fel: faltfel })}
          />
          <Kryssrad
            ikryssad={andratRot}
            onToggle={() => sattAndratRot(!andratRot)}
            disabled={last}
          >
            Ger ROT
          </Kryssrad>
          <Tomtext>{ROTHJALP}</Tomtext>
          {/*
            SANNINGEN OM EN ÄNDRING, och den är lätt att gissa fel på: varken
            namnet eller ROT-krysset fryses på tidraden. Fakturaunderlaget slår
            upp dem i task-tabellen varje gång det tas fram
            (worker/src/modules/views/basis-view.ts, task_name och rot_eligible),
            så en ändring slår igenom bakåt — även på rader som redan är
            fakturamärkta. Det är därför skärmen ber om ett NYTT moment i stället
            för att skriva om ett gammalt.
          */}
          <Tomtext>
            Namnet och ROT-krysset läses om varje gång ett underlag tas fram. En
            ändring gäller alltså också tid som redan är rapporterad, och även
            underlag som redan gått ut. Ska firman börja göra något annat: lägg
            upp ett nytt moment i stället.
          </Tomtext>

          <Knapp
            disabled={arLast || sparar}
            onClick={plan === null ? () => sparaAndringen(andrarRad) : forsokIgen}
            title={arLast ? 'Prenumerationen är pausad. Inget nytt kan sparas.' : undefined}
          >
            {sparar
              ? 'Sparar ...'
              : plan === null
                ? 'Spara ändringen'
                : FORSOKSTEXT[plan.slag]}
          </Knapp>

          {!last && !fragarOmRadering && (
            <Knapp variant="stor-tyst" onClick={() => sattFragarOmRadering(true)}>
              Ta bort momentet
            </Knapp>
          )}
          {!last && fragarOmRadering && (
            <>
              <Snabbrad>
                <Knapp variant="snabb" onClick={() => sattFragarOmRadering(false)}>
                  Behåll
                </Knapp>
                <Knapp
                  variant="snabb"
                  disabled={arLast || sparar}
                  onClick={() => taBort(andrarRad)}
                >
                  Ta bort {andrarRad.name}
                </Knapp>
              </Snabbrad>
              <Tomtext>
                Momentet försvinner ur listan men står kvar på tid som redan är
                rapporterad — texten på gamla fakturaunderlag ändras inte.
              </Tomtext>
            </>
          )}

          {!last && (
            <Knapp variant="stor-tyst" onClick={() => sattAndrarId(null)}>
              Avbryt
            </Knapp>
          )}
        </Yta>
      )}

      {/* --- Lägg till ett moment --------------------------------------- */}
      {arChef && andrarRad === null && oppet && (
        <Yta rubrik="Nytt moment">
          <Falt
            etikett="Vad heter momentet?"
            varde={namn}
            onAndra={(t) => {
              sattFaltfel(null);
              sattNamn(t);
            }}
            disabled={last}
            maxLength={MAX_MOMENTNAMN_TECKEN}
            placeholder="Provtryckning och täthetskontroll"
            hjalp="Skriv det som en kund ska kunna läsa på sin faktura — inte en förkortning."
            {...(faltfel === null ? {} : { fel: faltfel })}
          />
          <Kryssrad ikryssad={gerRot} onToggle={() => sattGerRot(!gerRot)} disabled={last}>
            Ger ROT
          </Kryssrad>
          <Tomtext>{ROTHJALP}</Tomtext>

          <Knapp
            disabled={arLast || sparar}
            onClick={plan === null ? laggTill : forsokIgen}
            title={arLast ? 'Prenumerationen är pausad. Inget nytt kan sparas.' : undefined}
          >
            {sparar
              ? 'Sparar ...'
              : plan === null
                ? 'Spara momentet'
                : FORSOKSTEXT[plan.slag]}
          </Knapp>
          {!last && (
            <Knapp variant="stor-tyst" onClick={() => sattOppet(false)}>
              Avbryt
            </Knapp>
          )}
        </Yta>
      )}

      {fel !== null && <Felruta fel={fel} />}
      {!sparar && slappPlanen}

      {arChef && andrarRad === null && !oppet && (
        <Knapp
          variant={moment.length === 0 ? 'stor' : 'stor-tyst'}
          disabled={arLast}
          onClick={() => {
            sattFel(null);
            sattFaltfel(null);
            sattOppet(true);
          }}
          title={arLast ? 'Prenumerationen är pausad. Inget nytt kan sparas.' : undefined}
        >
          Lägg till ett moment
        </Knapp>
      )}

      {arLast && arChef && (
        <Tomtext>
          Prenumerationen är pausad. Allt går att läsa och exportera, men inget
          nytt kan sparas förrän betalningen är ordnad.
        </Tomtext>
      )}

      <Tomtext>
        Momenten hamnar ordagrant på kundens faktura. Det är därför de finns, och
        därför de är obligatoriska mot privatkund och frivilliga mot företag. Ute
        på jobbet bockar montören i dem — han skriver ingen text själv.
      </Tomtext>
    </>
  );
};
